import { DEMO_MAX_DURATION_SECONDS } from '../shared/demo-limits';
import { waitForSummary, requestSession } from './api';
import type { DemoView } from './view';

/**
 * One live conversation: session token → WebRTC call in the browser → summary card.
 * The SDK is loaded on demand so the sample mode stays light.
 */
export async function runLiveDemo(view: DemoView, turnstileToken: string): Promise<void> {
  const { conversationToken, demoId } = await requestSession(turnstileToken);
  const { Conversation } = await import('@elevenlabs/client');

  view.startConversation('Соединяемся…');
  let elapsed = 0;

  // Resolves with the id reported by onConnect, or null if the call never connected.
  const conversationId = await new Promise<string | null>((resolve, reject) => {
    let timer: ReturnType<typeof setInterval> | undefined;
    let connectedId: string | null = null;
    Conversation.startSession({
      conversationToken,
      connectionType: 'webrtc',
      onConnect: ({ conversationId: id }) => {
        connectedId = id;
        view.setStatus('Идёт разговор', true);
        timer = setInterval(() => {
          elapsed += 1;
          view.setTimer(elapsed);
        }, 1000);
      },
      onMessage: ({ role, message }) => {
        if (message.trim()) view.addTurn(role === 'agent' ? 'vera' : 'parent', message);
      },
      onDisconnect: () => {
        clearInterval(timer);
        resolve(connectedId);
      },
      onError: (message) => {
        clearInterval(timer);
        reject(new Error(message));
      },
    }).then((conversation) => {
      setTimeout(() => void conversation.endSession(), DEMO_MAX_DURATION_SECONDS * 1000);
    }, reject);
  });

  view.setStatus('Готовим сводку…', false);
  if (conversationId === null) throw new Error('Conversation did not connect');
  const summary = await waitForSummary({ demoId, conversationId });
  view.setStatus('Разговор завершён', false);
  view.showSummary(summary, 'Сводка по вашему разговору');
}
